// Copyright (c) 2025 Annadaneshwar Yadawad. All rights reserved.

import { useState } from 'react';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import Paywall from '../components/Paywall';
import { useSubscription } from '../components/SubscriptionContext';
import './Services.css';

const aiFeatures = [
  'Face detection with Azure',
  'Image analysis insights',
  'Speech ↔ Text conversions',
  'Single ₹5 unlock for all AI services',
];

const FaceDetection = () => {
  const { user } = useAuth0();
  const email = user?.email;
  const { isSubscribedForEmail, isFeatureUnlocked, subscribeForFeature } = useSubscription();
  const [file, setFile] = useState(null);
  const [faces, setFaces] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');

  const isSubscribed = isSubscribedForEmail(email) || isFeatureUnlocked('ai');

  if (!isSubscribed) {
    return (
      <div className="service-page">
        <Paywall
          title="Unlock AI Services"
          description="Subscribe for ₹5 to run face detection and other AI tools."
          onSubscribe={() => subscribeForFeature('ai')}
          ctaLabel="Subscribe for ₹5"
          priceLabel="₹5"
          features={aiFeatures}
        />
      </div>
    );
  }

  const handleDetect = async () => {
    if (!file) {
      setError('Please select a PNG or JPG image.');
      return;
    }

    try {
      setLoading(true);
      setStatus('Analyzing faces...');
      setError(null);
      setFaces([]);

      const response = await axios.post(
        'https://azurefaceapis.cognitiveservices.azure.com/face/v1.0/detect?returnFaceAttributes=age,gender,emotion',
        file,
        {
          headers: {
            'Content-Type': 'application/octet-stream',
            'Ocp-Apim-Subscription-Key': process.env.REACT_APP_FACE_KEY
          }
        }
      );

      setFaces(response.data);
      if (response.data.length === 0) {
        setError('No faces detected in the uploaded image.');
      }
    } catch (err) {
      const message = err?.response?.data?.error?.message;
      const code = err?.response?.data?.error?.code;
      if (code === 'BadArgument' && message?.includes('Invalid Media Type')) {
        setError('Please upload a PNG or JPG format image.');
      } else {
        setError('Error detecting faces. Please try again.');
        console.error(err);
      }
      setFaces([]);
    } finally {
      setLoading(false);
      setStatus('');
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (
      selectedFile &&
      !["image/jpeg", "image/jpg", "image/png"].includes(selectedFile.type)
    ) {
      alert("Please select a valid PNG or JPG file.");
      setFile(null);
      return;
    }
    setFile(selectedFile);
  };

  return (
    <div className="service-page ai-face">
      <div className="service-heading">
        <div>
          <h1>Face Detection</h1>
          <p className="service-description">Detect faces, attributes, and emotions with Azure Face API.</p>
        </div>
        <div className="service-actions">
          <span className="action-chip">AI Services</span>
          <span className="action-chip">Face Detection</span>
        </div>
      </div>

      <div className="service-grid">
        <div className="service-form">
          <label>
            Upload image (PNG or JPG)
            <input
              type="file"
              accept="image/jpeg,image/png"
              onChange={handleFileChange}
            />
          </label>
          <button
            onClick={handleDetect}
            className="primary-btn"
            disabled={loading}
          >
            {loading ? 'Detecting…' : 'Detect Faces'}
          </button>
          {status && (
            <div className="status-banner">
              <span className="status-icon check">★</span>
              <span>{status}</span>
            </div>
          )}
          {error && (
            <div className="status-banner error">
              <span className="status-icon error">!</span>
              <span>{error}</span>
            </div>
          )}
        </div>

        {faces.length > 0 && (
          <div className="analysis-grid">
            {faces.map((face, idx) => (
              <div key={idx} className="analysis-card">
                <p className="analysis-label">Face #{idx + 1}</p>
                <div className="meta-grid">
                  <div><span className="meta-label">Top</span><span className="meta-value">{face.faceRectangle.top}</span></div>
                  <div><span className="meta-label">Left</span><span className="meta-value">{face.faceRectangle.left}</span></div>
                  <div><span className="meta-label">Width</span><span className="meta-value">{face.faceRectangle.width}</span></div>
                  <div><span className="meta-label">Height</span><span className="meta-value">{face.faceRectangle.height}</span></div>
                </div>
                {face.faceAttributes && (
                  <div className="meta-grid" style={{ marginTop: '8px' }}>
                    <div><span className="meta-label">Age</span><span className="meta-value">{face.faceAttributes.age}</span></div>
                    <div><span className="meta-label">Gender</span><span className="meta-value">{face.faceAttributes.gender}</span></div>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <span className="meta-label">Emotions</span>
                      <span className="meta-value">{Object.entries(face.faceAttributes.emotion || {}).map(([k,v]) => `${k}:${(v*100).toFixed(1)}%`).join('  ')}</span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FaceDetection;